import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Link as MuiLink,
  TextField,
  Button,
  IconButton,
  Grid,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import CurrencyTextField, { formatCurrency } from '../CurrencyTextField';
import {
  hasProposalBreakdownAmounts,
  resolveProposalTotalAmount,
  sumProposalBreakdownAmounts
} from '../../utils/proposalBreakdown';

export function parseJsonbStringArray(val) {
  if (val == null) return [];
  if (Array.isArray(val)) return val.map((s) => String(s || '').trim()).filter(Boolean);
  if (typeof val === 'string') {
    try {
      const p = JSON.parse(val);
      return Array.isArray(p) ? p.map((s) => String(s || '').trim()).filter(Boolean) : [];
    } catch {
      return [];
    }
  }
  return [];
}

export function normalizeSowListStyle(v) {
  const s = String(v == null || v === '' ? 'bullets' : v).trim().toLowerCase();
  if (s === 'numbered' || s === 'number') return 'numbered';
  if (s === 'none' || s === 'plain') return 'none';
  return 'bullets';
}

function orderHasOwnSowData(order) {
  const scope = parseJsonbStringArray(order?.sow_scope_sections);
  const clar = parseJsonbStringArray(order?.sow_clarifications_sections);
  const total = Number(order?.proposal_total_amount) || 0;
  const mat = Number(order?.proposal_materials_amount) || 0;
  const lab = Number(order?.proposal_labor_amount) || 0;
  const rent = Number(order?.proposal_rentals_amount) || 0;
  return (
    scope.length > 0 ||
    clar.length > 0 ||
    total > 0 ||
    mat > 0 ||
    lab > 0 ||
    rent > 0 ||
    !!order?.proposal_show_breakdown
  );
}

/** Resolve Field Work display fields: prefer order-owned columns, fall back to linked quote for legacy rows. */
export function resolveOrderFieldWorkFields(order) {
  const useOwn = orderHasOwnSowData(order);
  const scope = useOwn
    ? parseJsonbStringArray(order?.sow_scope_sections)
    : parseJsonbStringArray(order?.source_quote_sow_scope_sections);
  const clar = useOwn
    ? parseJsonbStringArray(order?.sow_clarifications_sections)
    : parseJsonbStringArray(order?.source_quote_sow_clarifications_sections);
  const listStyle = normalizeSowListStyle(
    useOwn ? order?.sow_list_style : order?.source_quote_sow_list_style
  );
  const showBreakdown = useOwn
    ? !!order?.proposal_show_breakdown
    : !!order?.source_quote_proposal_show_breakdown;
  const materials = useOwn
    ? Number(order?.proposal_materials_amount) || 0
    : Number(order?.source_quote_proposal_materials_amount) || 0;
  const labor = useOwn
    ? Number(order?.proposal_labor_amount) || 0
    : Number(order?.source_quote_proposal_labor_amount) || 0;
  const rentals = useOwn
    ? Number(order?.proposal_rentals_amount) || 0
    : Number(order?.source_quote_proposal_rentals_amount) || 0;
  const proposalTotalRaw = useOwn
    ? order?.proposal_total_amount
    : order?.source_quote_proposal_total_amount;
  const proposalTotal =
    proposalTotalRaw != null && proposalTotalRaw !== ''
      ? resolveProposalTotalAmount({
          proposal_total_amount: proposalTotalRaw,
          proposal_materials_amount: materials,
          proposal_labor_amount: labor,
          proposal_rentals_amount: rentals
        })
      : null;
  return {
    scope,
    clar,
    listStyle,
    showBreakdown,
    materials,
    labor,
    rentals,
    proposalTotal
  };
}

export function fieldWorkFieldsFromOrder(order) {
  const resolved = resolveOrderFieldWorkFields(order);
  return {
    sow_scope_sections: resolved.scope.length ? resolved.scope : [''],
    sow_clarifications_sections: resolved.clar.length ? resolved.clar : [''],
    proposal_total_amount: resolved.proposalTotal != null ? resolved.proposalTotal : 0,
    proposal_materials_amount: resolved.materials,
    proposal_labor_amount: resolved.labor,
    proposal_rentals_amount: resolved.rentals,
    proposal_show_breakdown: resolved.showBreakdown,
    sow_list_style: resolved.listStyle
  };
}

/** Print layout: ul / ol / plain blocks (matches Quote print). */
export function renderPrintSowItemList(items, listStyle, keyPrefix) {
  const style = normalizeSowListStyle(listStyle);
  if (!items.length) return null;
  const itemClass = 'print-sow-paragraph';
  const preWrap = { whiteSpace: 'pre-wrap' };
  if (style === 'numbered') {
    return (
      <ol className="print-sow-list">
        {items.map((text, i) => (
          <li key={`${keyPrefix}-${i}`} className={itemClass} style={preWrap}>
            {text}
          </li>
        ))}
      </ol>
    );
  }
  if (style === 'none') {
    return (
      <div className="print-sow-plain">
        {items.map((text, i) => (
          <div key={`${keyPrefix}-${i}`} className={itemClass} style={preWrap}>
            {text}
          </div>
        ))}
      </div>
    );
  }
  return (
    <ul className="print-sow-list">
      {items.map((text, i) => (
        <li key={`${keyPrefix}-${i}`} className={itemClass} style={preWrap}>
          {text}
        </li>
      ))}
    </ul>
  );
}

const screenListItemTypographySx = {
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  maxWidth: '100%'
};

function renderScreenList(items, listStyle) {
  const style = normalizeSowListStyle(listStyle);
  if (!items.length) return <Typography variant="body2" color="text.secondary">—</Typography>;
  if (style === 'numbered') {
    return (
      <Box component="ol" sx={{ pl: 2.5, m: 0, minWidth: 0, '& li': { mb: 1 } }}>
        {items.map((text, i) => (
          <Typography key={i} component="li" variant="body2" sx={screenListItemTypographySx}>
            {text}
          </Typography>
        ))}
      </Box>
    );
  }
  if (style === 'none') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
        {items.map((text, i) => (
          <Typography key={i} variant="body2" sx={screenListItemTypographySx}>
            {text}
          </Typography>
        ))}
      </Box>
    );
  }
  return (
    <Box component="ul" sx={{ pl: 2.5, m: 0, minWidth: 0, listStyleType: 'disc', '& li': { mb: 1 } }}>
      {items.map((text, i) => (
        <Typography key={i} component="li" variant="body2" sx={screenListItemTypographySx}>
          {text}
        </Typography>
      ))}
    </Box>
  );
}

/**
 * Field Work / Service panel on Order Detail — editable when order is in edit mode.
 */
export function FieldWorkOrderDetailCard({ order, editing = false, editedOrder, onPatch, readOnly = false }) {
  const displayOrder = editing && editedOrder ? { ...order, ...editedOrder } : order;
  const canEdit = editing && !readOnly && typeof onPatch === 'function';
  const resolved = resolveOrderFieldWorkFields(displayOrder);
  const { scope, clar, listStyle, showBreakdown, materials, labor, rentals, proposalTotal } = resolved;
  const hasFieldWorkContent =
    scope.length > 0 ||
    clar.length > 0 ||
    (proposalTotal != null && proposalTotal > 0) ||
    showBreakdown ||
    canEdit;

  const patch = (updates) => {
    if (canEdit) onPatch(updates);
  };

  const handleScopeChange = (index, value) => {
    const sections = [...(displayOrder.sow_scope_sections || [''])];
    sections[index] = value;
    patch({ sow_scope_sections: sections });
  };

  const handleAddScope = () => {
    const sections = [...(displayOrder.sow_scope_sections || [''])];
    sections.push('');
    patch({ sow_scope_sections: sections });
  };

  const handleRemoveScope = (index) => {
    const sections = [...(displayOrder.sow_scope_sections || [''])];
    sections.splice(index, 1);
    patch({ sow_scope_sections: sections.length ? sections : [''] });
  };

  const handleClarChange = (index, value) => {
    const sections = [...(displayOrder.sow_clarifications_sections || [''])];
    sections[index] = value;
    patch({ sow_clarifications_sections: sections });
  };

  const handleAddClar = () => {
    const sections = [...(displayOrder.sow_clarifications_sections || [''])];
    sections.push('');
    patch({ sow_clarifications_sections: sections });
  };

  const handleRemoveClar = (index) => {
    const sections = [...(displayOrder.sow_clarifications_sections || [''])];
    sections.splice(index, 1);
    patch({ sow_clarifications_sections: sections.length ? sections : [''] });
  };

  const handleBreakdownChange = (field) => (e) => {
    const rawValue = e.target.value;
    const next = { ...displayOrder, [field]: rawValue };
    const updates = { [field]: rawValue };
    if (
      hasProposalBreakdownAmounts(
        next.proposal_materials_amount,
        next.proposal_labor_amount,
        next.proposal_rentals_amount
      )
    ) {
      updates.proposal_total_amount = sumProposalBreakdownAmounts(
        next.proposal_materials_amount,
        next.proposal_labor_amount,
        next.proposal_rentals_amount
      );
    }
    patch(updates);
  };

  const scopeSections = canEdit
    ? displayOrder.sow_scope_sections || ['']
    : scope.length
      ? scope
      : [''];
  const clarSections = canEdit
    ? displayOrder.sow_clarifications_sections || ['']
    : clar.length
      ? clar
      : [];

  return (
    <Card sx={{ mb: 1.5, minWidth: 0, maxWidth: '100%' }}>
      <CardContent sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
            Field Work / Service
          </Typography>
          {order?.quote_id != null ? (
            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere', maxWidth: '100%' }}>
              Linked quote{' '}
              <MuiLink component={Link} to={`/quotes/${order.quote_id}`} underline="hover">
                {order.quote_identifier || order.quote_number || `#${order.quote_id}`}
              </MuiLink>
              . Scope and proposal on this order can be edited here and may differ from the quote.
            </Typography>
          ) : null}
        </Box>

        {hasFieldWorkContent || canEdit ? (
          <>
            <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
              Scope of work
            </Typography>
            {scopeSections.map((section, index) => (
              <Box key={`order-sow-scope-${index}`} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label={index === 0 ? 'Scope' : `Scope ${index + 1}`}
                  value={section}
                  size="small"
                  InputProps={{ readOnly: !canEdit }}
                  onChange={(e) => handleScopeChange(index, e.target.value)}
                />
                {canEdit ? (
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveScope(index)}
                    disabled={(displayOrder.sow_scope_sections || []).length <= 1}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                ) : null}
              </Box>
            ))}
            {canEdit ? (
              <Button variant="outlined" size="small" onClick={handleAddScope} sx={{ mb: 2 }}>
                Add scope section
              </Button>
            ) : scope.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No scope sections yet.
              </Typography>
            ) : null}

            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Clarifications and exceptions
            </Typography>
            {(canEdit ? clarSections.length > 0 ? clarSections : [''] : clar).map((section, index) => (
              <Box key={`order-sow-clar-${index}`} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label={index === 0 ? 'Clarification' : `Clarification ${index + 1}`}
                  value={section}
                  size="small"
                  InputProps={{ readOnly: !canEdit }}
                  onChange={(e) => handleClarChange(index, e.target.value)}
                />
                {canEdit ? (
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveClar(index)}
                    disabled={(displayOrder.sow_clarifications_sections || []).length <= 1}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                ) : null}
              </Box>
            ))}
            {canEdit ? (
              <Button variant="outlined" size="small" onClick={handleAddClar} sx={{ mb: 1.5 }}>
                Add clarification section
              </Button>
            ) : null}

            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <CurrencyTextField
                  fullWidth
                  size="small"
                  label="Proposal total amount"
                  dollarAdornment
                  minimumFractionDigits={2}
                  maximumFractionDigits={2}
                  value={displayOrder.proposal_total_amount ?? ''}
                  onChange={(e) => patch({ proposal_total_amount: e.target.value })}
                  InputProps={{ readOnly: !canEdit }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <CurrencyTextField
                  fullWidth
                  size="small"
                  label="Materials"
                  dollarAdornment
                  minimumFractionDigits={2}
                  maximumFractionDigits={2}
                  value={displayOrder.proposal_materials_amount ?? ''}
                  onChange={canEdit ? handleBreakdownChange('proposal_materials_amount') : undefined}
                  InputProps={{ readOnly: !canEdit }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <CurrencyTextField
                  fullWidth
                  size="small"
                  label="Labor"
                  dollarAdornment
                  minimumFractionDigits={2}
                  maximumFractionDigits={2}
                  value={displayOrder.proposal_labor_amount ?? ''}
                  onChange={canEdit ? handleBreakdownChange('proposal_labor_amount') : undefined}
                  InputProps={{ readOnly: !canEdit }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <CurrencyTextField
                  fullWidth
                  size="small"
                  label="Rentals"
                  dollarAdornment
                  minimumFractionDigits={2}
                  maximumFractionDigits={2}
                  value={displayOrder.proposal_rentals_amount ?? ''}
                  onChange={canEdit ? handleBreakdownChange('proposal_rentals_amount') : undefined}
                  InputProps={{ readOnly: !canEdit }}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75 }}>
                Printed version options
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!displayOrder.proposal_show_breakdown}
                    onChange={(e) => patch({ proposal_show_breakdown: e.target.checked })}
                    disabled={!canEdit}
                  />
                }
                label="Show price breakdown on print version"
              />
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Scope list style (print)
                </Typography>
                <RadioGroup
                  row
                  value={normalizeSowListStyle(displayOrder.sow_list_style)}
                  onChange={(e) => patch({ sow_list_style: e.target.value })}
                >
                  <FormControlLabel value="bullets" control={<Radio size="small" disabled={!canEdit} />} label="Bullets" />
                  <FormControlLabel value="numbered" control={<Radio size="small" disabled={!canEdit} />} label="Numbered" />
                  <FormControlLabel value="none" control={<Radio size="small" disabled={!canEdit} />} label="Plain" />
                </RadioGroup>
              </Box>
            </Box>

            {!canEdit ? (
              <Box sx={{ mt: 2, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75 }}>
                  Proposal summary
                </Typography>
                {showBreakdown ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', maxWidth: 360 }}>
                      <Typography variant="body2">Materials</Typography>
                      <Typography variant="body2">{formatCurrency(materials)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', maxWidth: 360 }}>
                      <Typography variant="body2">Labor</Typography>
                      <Typography variant="body2">{formatCurrency(labor)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', maxWidth: 360 }}>
                      <Typography variant="body2">Rentals</Typography>
                      <Typography variant="body2">{formatCurrency(rentals)}</Typography>
                    </Box>
                  </Box>
                ) : null}
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Total proposal amount:{' '}
                  {proposalTotal != null && Number.isFinite(proposalTotal) ? formatCurrency(proposalTotal) : '—'}
                </Typography>
              </Box>
            ) : null}

            {String(order.notes ?? '').trim() && !canEdit ? (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                  Comments
                </Typography>
                <Typography variant="body2" sx={screenListItemTypographySx}>
                  {String(order.notes).trim()}
                </Typography>
              </>
            ) : null}
          </>
        ) : (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {canEdit
                ? 'Add scope sections and proposal amounts for this Field Work order.'
                : 'No scope or proposal on this order yet. Edit the order to add Field Work details, or use description and notes below.'}
            </Typography>
            {canEdit ? (
              <Button variant="outlined" size="small" onClick={handleAddScope}>
                Add scope section
              </Button>
            ) : (
              <>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Description
                </Typography>
                <Typography variant="body2" sx={{ ...screenListItemTypographySx, mb: order.notes ? 2 : 0 }}>
                  {order.description?.trim() || '—'}
                </Typography>
                {order.notes ? (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Notes
                    </Typography>
                    <Typography variant="body2" sx={screenListItemTypographySx}>
                      {order.notes}
                    </Typography>
                  </>
                ) : null}
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Print-only fragment: Field Work blocks (order-owned), or fallback description.
 */
export function FieldWorkOrderPrintSection({ order, hideDescriptionBlock, omitFinancial = false }) {
  const { scope, clar, listStyle, showBreakdown, materials, labor, rentals, proposalTotal } =
    resolveOrderFieldWorkFields(order);
  const hasFieldWorkContent =
    scope.length > 0 || clar.length > 0 || (proposalTotal != null && proposalTotal > 0) || showBreakdown;

  if (!hasFieldWorkContent) {
    if (hideDescriptionBlock) return null;
    if (!order.description?.trim()) return null;
    return (
      <div className="print-section" style={{ marginBottom: '20px', marginTop: '10px' }}>
        <strong>Description:</strong> {order.description}
      </div>
    );
  }

  return (
    <>
      <div className="print-sow-block" style={{ marginBottom: 12 }}>
        <div className="print-sow-heading" style={{ fontSize: '13px', marginTop: 8 }}>
          Scope of work
        </div>
        {scope.length > 0 ? (
          renderPrintSowItemList(scope, listStyle, 'print-order-sow-scope')
        ) : (
          <div style={{ color: '#666' }}>No scope provided.</div>
        )}
      </div>
      {clar.length > 0 ? (
        <div className="print-sow-block" style={{ marginBottom: 12 }}>
          <div className="print-sow-heading">Clarifications and exceptions</div>
          {renderPrintSowItemList(clar, listStyle, 'print-order-sow-clar')}
        </div>
      ) : null}
      {!omitFinancial && showBreakdown ? (
        <table className="print-table print-quote-line-table print-table-no-zebra" style={{ border: 'none', marginBottom: 8 }}>
          <tbody>
            <tr>
              <td style={{ border: 'none', padding: '6px 8px', fontWeight: 600 }}>Materials</td>
              <td style={{ border: 'none', padding: '6px 8px', textAlign: 'right' }}>{formatCurrency(materials)}</td>
            </tr>
            <tr>
              <td style={{ border: 'none', padding: '6px 8px', fontWeight: 600 }}>Labor</td>
              <td style={{ border: 'none', padding: '6px 8px', textAlign: 'right' }}>{formatCurrency(labor)}</td>
            </tr>
            <tr>
              <td style={{ border: 'none', padding: '6px 8px', fontWeight: 600 }}>Rentals</td>
              <td style={{ border: 'none', padding: '6px 8px', textAlign: 'right' }}>{formatCurrency(rentals)}</td>
            </tr>
          </tbody>
        </table>
      ) : null}
      {!omitFinancial ? (
        <div className="print-quote-total-row" style={{ marginBottom: 16 }}>
          <strong>Total proposal amount:</strong>{' '}
          <span>{proposalTotal != null && Number.isFinite(proposalTotal) ? formatCurrency(proposalTotal) : '—'}</span>
        </div>
      ) : null}
    </>
  );
}
